import { useMemo, useState } from "react";
import { ArrowRight, Brain, Check, RotateCcw, X } from "lucide-react";
import type { InSnipCard } from "./types";

/**
 * Recall: a calm, spaced-repetition review of the ideas you've kept. This is the knowledge
 * companion's quiet superpower, instead of endless scrolling, InSnip brings an idea back right
 * before you'd forget it, so reading turns into remembering.
 *
 * Leitner-style: every idea sits in a box. Get it right and it moves up a box and resurfaces
 * later; miss it and it drops to box 0 and comes back soon. Fully self-contained: the deck is
 * passed in, progress persists in localStorage, no network needed (works on the static feed too).
 */
const RECALL_KEY = "insnip.recall";
const DAY_MS = 86_400_000;
// Days before an idea in each box is due again. Higher box = longer gap (it has stuck better).
const BOX_INTERVAL_DAYS = [0, 1, 3, 7, 16, 35];

type RecallEntry = { box: number; last: number };
type RecallState = Record<string, RecallEntry>;

function loadRecall(): RecallState {
  try {
    const raw = localStorage.getItem(RECALL_KEY);
    return raw ? (JSON.parse(raw) as RecallState) : {};
  } catch {
    return {};
  }
}

function saveRecall(state: RecallState): void {
  try {
    localStorage.setItem(RECALL_KEY, JSON.stringify(state));
  } catch {
    /* storage unavailable, recall just won't persist this session */
  }
}

/** True when an idea has never been reviewed, or its box interval has elapsed. */
export function recallDue(entry: RecallEntry | undefined, now: number): boolean {
  if (!entry) return true;
  const days = BOX_INTERVAL_DAYS[Math.min(entry.box, BOX_INTERVAL_DAYS.length - 1)];
  return now >= entry.last + days * DAY_MS;
}

/** How many of these cards are due for recall right now (powers the menu badge). */
export function dueCount(cards: InSnipCard[]): number {
  const state = loadRecall();
  const now = Date.now();
  return cards.reduce((n, c) => n + (recallDue(state[c.id], now) ? 1 : 0), 0);
}

export function RecallModal(props: {
  deck: InSnipCard[];
  onClose: () => void;
  onOpenCard?: (card: InSnipCard) => void;
}) {
  const [recall, setRecall] = useState<RecallState>(() => loadRecall());

  // Order the deck once on open: due ideas first (most overdue leading), then the rest. A short
  // session always refreshes what's most at risk of fading.
  const ordered = useMemo(() => {
    const now = Date.now();
    const snapshot = loadRecall();
    const due: InSnipCard[] = [];
    const rest: InSnipCard[] = [];
    for (const card of props.deck) {
      (recallDue(snapshot[card.id], now) ? due : rest).push(card);
    }
    due.sort((a, b) => (snapshot[a.id]?.last ?? 0) - (snapshot[b.id]?.last ?? 0));
    return [...due, ...rest];
  }, [props.deck]);

  const [idx, setIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [reviewed, setReviewed] = useState(0);
  const [done, setDone] = useState(ordered.length === 0);

  const total = ordered.length;
  const card = ordered[idx];
  const libraryCount = props.deck.length;

  function rate(remembered: boolean): void {
    if (!card) return;
    const prevBox = recall[card.id]?.box ?? 0;
    const next: RecallState = {
      ...recall,
      [card.id]: {
        box: remembered ? Math.min(prevBox + 1, BOX_INTERVAL_DAYS.length - 1) : 0,
        last: Date.now(),
      },
    };
    setRecall(next);
    saveRecall(next);
    setReviewed((n) => n + 1);
    if (idx + 1 < total) {
      setIdx(idx + 1);
      setRevealed(false);
    } else {
      setDone(true);
    }
  }

  return (
    <section
      className="insight-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Recall"
      onClick={props.onClose}
    >
      <div className="recall-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="quiz-close icon-button ghost"
          onClick={props.onClose}
          aria-label="Close recall"
        >
          <X size={18} />
        </button>

        {total === 0 ? (
          <div className="recall-result">
            <div className="recall-result-icon" aria-hidden="true"><Brain size={28} /></div>
            <h2>Nothing to recall yet</h2>
            <p>
              Bookmark the cards worth remembering. InSnip will quietly bring them back, right
              before you'd forget, so the ideas actually stick.
            </p>
            <button type="button" className="ghost-action" onClick={props.onClose}>Got it</button>
          </div>
        ) : done ? (
          <div className="recall-result">
            <div className="recall-result-icon" aria-hidden="true"><Brain size={28} /></div>
            <span className="recall-eyebrow">Recall</span>
            <h2>{reviewed ? `You refreshed ${reviewed} idea${reviewed === 1 ? "" : "s"}` : "All caught up"}</h2>
            <p>
              {reviewed
                ? "Knowledge sticks when you revisit it. We'll bring these back again, spaced a little further out each time."
                : "Nothing is due right now. Come back tomorrow to keep your ideas sharp."}
            </p>
            <div className="recall-result-meta">
              {libraryCount} idea{libraryCount === 1 ? "" : "s"} in your library
            </div>
            <button type="button" className="ghost-action" onClick={props.onClose}>Done</button>
          </div>
        ) : (
          <>
            <div className="recall-head">
              <span className="recall-eyebrow"><Brain size={13} aria-hidden="true" /> Recall</span>
              <span className="recall-progress">{idx + 1} / {total}</span>
            </div>
            <div className="recall-bar" aria-hidden="true">
              <span style={{ width: `${(idx / total) * 100}%` }} />
            </div>

            <article className="recall-card">
              {card.category ? <span className="recall-cat">{card.category}</span> : null}
              <h2 className="recall-headline">{card.headline}</h2>

              {revealed ? (
                <div className="recall-reveal">
                  <p className="recall-essence">{card.essence}</p>
                  {card.whyItMatters ? (
                    <p className="recall-why"><span>Why it matters</span>{card.whyItMatters}</p>
                  ) : null}
                  {props.onOpenCard ? (
                    <button type="button" className="recall-open" onClick={() => props.onOpenCard?.(card)}>
                      Open the full card <ArrowRight size={13} />
                    </button>
                  ) : null}
                </div>
              ) : (
                <p className="recall-prompt">Do you remember why this matters?</p>
              )}
            </article>

            {revealed ? (
              <div className="recall-actions">
                <button type="button" className="recall-rate again" onClick={() => rate(false)}>
                  <RotateCcw size={15} aria-hidden="true" /> Bring it back soon
                </button>
                <button type="button" className="recall-rate got" onClick={() => rate(true)}>
                  <Check size={15} aria-hidden="true" /> I remembered
                </button>
              </div>
            ) : (
              <button type="button" className="recall-show" onClick={() => setRevealed(true)}>
                Reveal
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
}
